/**
 * Lightweight 401 event-bus.
 *
 * `api-client.ts` calls `onUnauthorized()` whenever it receives a 401 response.
 * `router.tsx` registers the concrete navigation handler via `setUnauthorizedHandler()`
 * as soon as the router is created.
 *
 * Keeping this in its own module breaks the otherwise-circular dependency chain:
 *
 *   query-client.ts  (leaf – no local imports)
 *   unauthorized-handler.ts  (leaf – no local imports)
 *   api-client.ts   ──imports──▶  unauthorized-handler.ts
 *   router.tsx      ──imports──▶  query-client.ts
 *                   ──imports──▶  unauthorized-handler.ts
 *
 * No cycles exist in this graph.
 */

type UnauthorizedHandler = () => void;

let handler: UnauthorizedHandler = () => {
  // No-op default handler in production and development to avoid noisy logs.
};

// Simple debounce to avoid invoking the handler repeatedly in a short window.
// Some parts of the app (api-client, hooks, routes) may all respond to the same
// 401 response and attempt navigation; debouncing here prevents multiple
// competing navigations which can cause nested router transitions and React's
// "Maximum update depth exceeded" error.
let lastInvocationTs = 0;
const INVOCATION_WINDOW_MS = 500;

/**
 * Invoke the registered unauthorized handler.
 *
 * Called by `ApiClient` when a 401 response is received.
 * This function defers the actual handler to a new task and debounces
 * multiple rapid calls.
 */
export const onUnauthorized = (): void => {
  // Respect a short navigation lock set after successful login/org selection
  try {
    // If we are in the post-login callback URL, skip handling unauthorized
    // because the server may be redirecting back after setting the session
    // cookie and an immediate 401 is expected until the cookie is visible.
    if (typeof window !== 'undefined' && window.location.search.includes('login=success')) {
      return;
    }
    const lock = sessionStorage.getItem('__nav_lock_until');
    if (lock) {
      const until = Number(lock);
      if (!Number.isNaN(until) && Date.now() < until) {
        return;
      }
    }
  } catch {
    // ignore storage errors and proceed
  }
  const now = Date.now();
  if (now - lastInvocationTs < INVOCATION_WINDOW_MS) {
    // Ignore excessive calls within the debounce window
    return;
  }

  lastInvocationTs = now;

  // Defer to next tick to avoid synchronous navigation during React commit.
  setTimeout(() => {
    try {
      // DEV-only instrumentation: log a compact marker + stack when the
      // global unauthorized handler is invoked so we can trace who triggered
      // the navigation during tricky login/logout races. This is intentionally
      // small and will be removed once we've captured the repro.
      // Call the registered handler. Temporary DEV-only diagnostic code
      // was removed to avoid committing debug instrumentation.
      handler();
    } catch {
      // Swallow errors from the handler to avoid disrupting the calling context.
    }
  }, 0);
};

/**
 * Register the function that should run when a 401 is received.
 *
 * Should be called once during app bootstrap — `router.tsx` does this
 * immediately after defining its `handleUnauthorized` function so that
 * the handler is in place before any API request can be made.
 */
export const setUnauthorizedHandler = (newHandler: UnauthorizedHandler): void => {
  handler = newHandler;
};
